import type { AvailableSlot } from './schemas';

export type SlotGroup = {
  dateKey: string;
  label: string;
  slots: AvailableSlot[];
};

/** Slot pagination stops fetching further out than this many days from the first fetched date. */
const PAGINATION_WINDOW_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Local-calendar-day key, matching `formatDateLabel`'s local rendering — a
 * UTC-derived key (`toISOString().slice(0, 10)`) can put a slot in a
 * different bucket than the day its label displays for viewers offset from
 * UTC, producing two chips with the same visible label. */
function localDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Inverse of `localDateKey` — parses a "YYYY-MM-DD" key as local midnight.
 * `new Date(dateKey)` parses a date-only ISO string as **UTC** midnight
 * (per spec), which drifts from the local calendar day the key actually
 * names for any viewer offset from UTC — e.g. a UTC-7 viewer's "2026-06-11"
 * parses to 2026-06-10T17:00 local, a different day. Always reconstruct via
 * the numeric constructor (local time) instead of re-parsing the string. */
export function parseLocalDateKey(dateKey: string): Date {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
}

export function groupSlotsByDate(slots: AvailableSlot[]): SlotGroup[] {
  const map = new Map<string, SlotGroup>();
  for (const slot of slots) {
    const date = new Date(slot.startTime);
    if (Number.isNaN(date.getTime())) continue;
    const dateKey = localDateKey(date);
    const existing = map.get(dateKey);
    if (existing) {
      existing.slots.push(slot);
    } else {
      map.set(dateKey, {
        dateKey,
        label: formatDateLabel(date),
        slots: [slot],
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.dateKey.localeCompare(b.dateKey));
}

export function formatDateLabel(date: Date): string {
  try {
    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return date.toDateString();
  }
}

export function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  try {
    return date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return date.toISOString();
  }
}

/** Merges two pages of slots, deduping by `startTime` and sorting ascending. */
export function mergeSlotPages(
  existing: AvailableSlot[],
  incoming: AvailableSlot[],
): AvailableSlot[] {
  const map = new Map<string, AvailableSlot>();
  for (const slot of existing) map.set(slot.startTime, slot);
  for (const slot of incoming) map.set(slot.startTime, slot);
  return Array.from(map.values()).sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );
}

/** True while `candidateDate` is within the 30-day pagination window of `firstFetchedDate`. */
export function withinPaginationWindow(firstFetchedDate: Date, candidateDate: Date): boolean {
  const diffDays = Math.abs(candidateDate.getTime() - firstFetchedDate.getTime()) / MS_PER_DAY;
  return diffDays <= PAGINATION_WINDOW_DAYS;
}
